import React, { useEffect, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import ValidationError from '../ValidationError/ValidationError';
import HelpText from '../HelpText/HelpText';
import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';
import { formatRequiredLabel } from '../../util/uiHelpers';
import { useIntl } from '../../util/reactIntl';

import css from './FieldSearchableSelect.module.css';

const MOBILE_MAX_WIDTH = 767;

/**
 * @typedef {Object} OptionConfig
 * @property {string} key
 * @property {string} label
 */

const SearchableSelectControl = props => {
  const {
    id,
    input,
    meta,
    label,
    helpText,
    options = [],
    isRequired,
    className,
    rootClassName,
    placeholder,
    searchPlaceholder,
    onValueChange,
    multiple = false,
  } = props;
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const skipBlurOnCloseRef = useRef(false);
  const onBlurRef = useRef(input.onBlur);
  onBlurRef.current = input.onBlur;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const update = () => setIsMobile(!!mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  const close = ({ blur = true, restoreFocus = true } = {}) => {
    setOpen(false);
    setQuery('');
    if (blur) {
      input.onBlur();
    }
    if (restoreFocus) {
      // Avoid marking untouched→touched via a second blur from focus restore races
      skipBlurOnCloseRef.current = true;
      triggerRef.current?.focus();
      // Allow future blurs after the focus settles
      setTimeout(() => {
        skipBlurOnCloseRef.current = false;
      }, 0);
    }
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = event => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
        onBlurRef.current();
        skipBlurOnCloseRef.current = true;
        triggerRef.current?.focus();
        setTimeout(() => {
          skipBlurOnCloseRef.current = false;
        }, 0);
      }
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const selectedKeys = multiple
    ? Array.isArray(input.value)
      ? input.value.filter(Boolean)
      : []
    : [];
  const selected = multiple ? null : options.find(o => o.key === input.value);
  const q = query.trim().toLowerCase();
  const available = multiple ? options.filter(o => !selectedKeys.includes(o.key)) : options;
  const filtered = q ? available.filter(o => (o.label || '').toLowerCase().includes(q)) : available;

  const displayLabel = formatRequiredLabel(label, isRequired);
  const classes = classNames(rootClassName || css.root, className);
  const triggerLabel =
    selected?.label ||
    placeholder ||
    intl.formatMessage({ id: 'FieldSearchableSelect.placeholder' });
  const labelId = `${id}-label`;
  const sheetTitleId = `${id}-sheet-title`;

  const selectOption = key => {
    if (multiple) {
      if (selectedKeys.includes(key)) {
        close({ blur: true, restoreFocus: true });
        return;
      }
      const nextValue = [...selectedKeys, key];
      input.onChange(nextValue);
      if (onValueChange) {
        onValueChange(nextValue);
      }
      close({ blur: true, restoreFocus: true });
      return;
    }
    // Optional fields: re-selecting the active option clears (matches FieldOptionGroup).
    const nextValue = !isRequired && key === input.value ? null : key;
    input.onChange(nextValue);
    if (onValueChange) {
      onValueChange(nextValue);
    }
    close({ blur: true, restoreFocus: true });
  };

  const removeSelected = key => {
    const nextValue = selectedKeys.filter(selectedKey => selectedKey !== key);
    input.onChange(nextValue);
    if (onValueChange) {
      onValueChange(nextValue);
    }
  };

  const list = (
    <>
      <div className={css.searchWrap}>
        <input
          ref={searchRef}
          className={css.search}
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={
            searchPlaceholder ||
            intl.formatMessage(
              { id: 'FieldSearchableSelect.searchPlaceholder' },
              { count: options.length }
            )
          }
          aria-label={intl.formatMessage({ id: 'FieldSearchableSelect.searchAriaLabel' })}
        />
      </div>
      <ul className={css.list} role="listbox" id={`${id}-listbox`}>
        {filtered.length === 0 ? (
          <li className={css.empty}>
            {intl.formatMessage({ id: 'FieldSearchableSelect.noMatches' })}
          </li>
        ) : (
          filtered.map(option => {
            const isSelected = option.key === input.value;
            return (
              <li key={option.key} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={classNames(css.row, { [css.rowSelected]: isSelected })}
                  onClick={() => selectOption(option.key)}
                >
                  <span className={css.tick} aria-hidden="true">
                    ✓
                  </span>
                  {option.label}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </>
  );

  return (
    <div className={classes}>
      {displayLabel ? (
        <label className={css.label} id={labelId} htmlFor={id}>
          {displayLabel}
        </label>
      ) : null}
      <OutsideClickHandler
        onOutsideClick={() => {
          if (open && !isMobile) {
            close({ blur: true, restoreFocus: false });
          }
        }}
      >
        <button
          ref={triggerRef}
          type="button"
          id={id}
          className={classNames(css.trigger, {
            [css.triggerOpen]: open,
            [css.triggerPlaceholder]: !selected,
          })}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-haspopup="listbox"
          aria-labelledby={displayLabel ? labelId : undefined}
          onFocus={input.onFocus}
          onBlur={() => {
            if (skipBlurOnCloseRef.current || open) {
              return;
            }
            input.onBlur();
          }}
          onClick={() => {
            if (open) {
              close({ blur: true, restoreFocus: true });
              return;
            }
            input.onFocus();
            setOpen(true);
          }}
        >
          <span className={css.triggerValue}>{triggerLabel}</span>
          <span className={css.chevron} aria-hidden="true">
            ▾
          </span>
        </button>

        {open && !isMobile ? <div className={css.panel}>{list}</div> : null}
      </OutsideClickHandler>

      {open && isMobile ? (
        <div
          className={css.sheetBackdrop}
          role="presentation"
          onClick={event => {
            if (event.target === event.currentTarget) {
              close({ blur: true, restoreFocus: true });
            }
          }}
        >
          <div
            className={css.sheet}
            role="dialog"
            aria-modal="true"
            aria-labelledby={displayLabel ? labelId : sheetTitleId}
          >
            <div className={css.sheetHeader}>
              <p className={css.sheetTitle} id={sheetTitleId}>
                {typeof label === 'string' ? label : displayLabel}
              </p>
              <button
                type="button"
                className={css.sheetClose}
                onClick={() => close({ blur: true, restoreFocus: true })}
              >
                {intl.formatMessage({ id: 'FieldSearchableSelect.close' })}
              </button>
            </div>
            {list}
          </div>
        </div>
      ) : null}

      {multiple && selectedKeys.length > 0 ? (
        <ul className={css.chips}>
          {selectedKeys.map(key => {
            const option = options.find(o => o.key === key);
            const chipLabel = option?.label || key;
            return (
              <li key={key} className={css.chipItem}>
                <button type="button" className={css.chip} onClick={() => removeSelected(key)}>
                  <span className={css.visuallyHidden}>
                    {intl.formatMessage({ id: 'FieldSearchableSelect.removeChipAction' })}
                  </span>
                  <span>{chipLabel}</span>
                  <span className={css.chipRemove} aria-hidden="true">
                    ×
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <HelpText helpText={helpText} />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form searchable select for long option lists (chipLimit+).
 * Desktop: popover panel. Mobile: bottom sheet. Esc + outside click close.
 * Set `multiple` to store an array and show selected values as removable chips.
 *
 * @component
 */
const FieldSearchableSelect = props => {
  const { name, validate, ...rest } = props;
  return (
    <Field name={name} validate={validate}>
      {fieldProps => <SearchableSelectControl {...rest} {...fieldProps} />}
    </Field>
  );
};

export default FieldSearchableSelect;
